/**
 * Phase 1 refinement Update 2 — Workspace Context Materials page.
 *
 * Mounted at:
 *   - /enterprise/settings/context  (enterprise_admin / enterprise_executive — own workspace)
 *   - /admin/workspace/:id/context  (platform_admin — any workspace, via impersonation/inspect)
 *
 * Captures the full set of materials that flavor the Assessment Generator's output.
 * Strict Realloc design tokens.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    Building2, BookOpen, Layers, Wrench, Palette, Files, Lock, Save, Loader2, Trash2, UploadCloud, CheckCircle2,
} from "lucide-react";

const cls = (...xs) => xs.filter(Boolean).join(" ");

const TABS = [
    { id: "identity",       label: "Identity",       icon: Building2 },
    { id: "about",          label: "About",          icon: BookOpen },
    { id: "industry",       label: "Industry",       icon: Layers },
    { id: "organizational", label: "Organizational", icon: Wrench },
    { id: "brand",          label: "Brand",          icon: Palette },
    { id: "documents",      label: "Documents",      icon: Files },
];

const FIELDS_BY_TAB = {
    identity: [
        ["company_name", "Company name", "input", { readOnly: true }],
        ["primary_industry", "Primary industry", "input", { readOnly: true }],
        ["company_website", "Company website", "input", { readOnly: true }],
        ["company_tagline", "One-line description / tagline", "input"],
        ["year_founded", "Year founded", "number"],
        ["headquarters_location", "HQ location", "input"],
        ["stock_ticker", "Stock ticker (if public)", "input"],
    ],
    about: [
        ["about_us", "About us", "textarea"],
        ["mission_statement", "Mission statement", "textarea"],
        ["vision_statement", "Vision statement", "textarea"],
        ["core_values_narrative", "Core values narrative (separate from the formal Values Framework)", "textarea"],
    ],
    industry: [
        ["sub_industry_specialization", "Sub-industry specialization", "input"],
        ["products_services", "Primary products / services", "textarea"],
        ["key_competitors", "Key competitors", "textarea"],
        ["regulatory_environment", "Regulatory environment", "textarea"],
    ],
    organizational: [
        ["organizational_structure", "Organizational structure", "select", { options: [["functional","Functional"],["divisional","Divisional"],["matrix","Matrix"],["flat","Flat"]] }],
        ["recent_transformations", "Recent transformations or initiatives", "textarea"],
        ["strategic_priorities", "Strategic priorities for the assessment cycle", "textarea"],
    ],
    brand: [
        ["logo_url", "Company logo URL", "input"],
        ["brand_color", "Brand accent color (hex, optional)", "input"],
        ["communication_tone", "Communication tone preference", "select", { options: [["formal","Formal"],["professional","Professional"],["conversational","Conversational"]] }],
    ],
};

export default function WorkspaceContextPage() {
    const { user, api } = useAuth();
    const params = useParams();
    const enterpriseId = params.id || user?.enterprise_id;

    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [tab, setTab] = useState("identity");
    const [busy, setBusy] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadMsg, setUploadMsg] = useState("");

    const refresh = useCallback(() => {
        if (!enterpriseId) return;
        api.get(`/v2/workspace/${enterpriseId}/context`)
            .then(r => { setData(r.data); setDraft(r.data.materials || {}); })
            .catch(() => {});
    }, [api, enterpriseId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (!enterpriseId) {
        return <div className="p-8 text-sm text-gray-500">No workspace context available for this account.</div>;
    }
    if (!data) {
        return <div className="p-12 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>;
    }

    const isDirty = JSON.stringify(draft) !== JSON.stringify(data.materials);

    const save = async () => {
        setBusy(true);
        try {
            const payload = {};
            const allKeys = Object.values(FIELDS_BY_TAB).flat().map(f => f[0]).filter(k => k && k !== "company_name" && k !== "primary_industry" && k !== "company_website");
            allKeys.forEach(k => { if (draft[k] !== undefined) payload[k] = draft[k] === "" ? null : draft[k]; });
            await api.put(`/v2/workspace/${enterpriseId}/context`, payload);
            setSavedAt(new Date().toLocaleTimeString());
            await refresh();
        } finally { setBusy(false); }
    };

    const upload = async (file, relevance_tag = "scenario_flavor") => {
        if (!file) return;
        setUploading(true); setUploadMsg("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("relevance_tag", relevance_tag);
            const r = await api.post(`/v2/workspace/${enterpriseId}/context/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploadMsg(`Uploaded "${r.data.filename}" — ${r.data.extracted_chars} chars extracted.`);
            await refresh();
        } catch (e) {
            setUploadMsg(`Upload failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeDoc = async (id) => {
        await api.delete(`/v2/workspace/${enterpriseId}/context/documents/${id}`);
        refresh();
    };

    const lockSnapshot = async () => {
        setBusy(true);
        try {
            await api.post(`/v2/workspace/${enterpriseId}/context/lock`, {});
            await refresh();
        } finally { setBusy(false); }
    };

    return (
        <div className="p-8 max-w-5xl space-y-6" data-testid="workspace-context-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-display font-bold text-white">Workspace Context</h1>
                    <p className="text-sm text-gray-500 mt-1 font-body">
                        Captures the customer-specific materials that flavor every assessment. Versioned and locked per cycle.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {savedAt && <span className="text-[10px] font-mono text-emerald-400">saved {savedAt}</span>}
                    <button onClick={save} disabled={!isDirty || busy}
                        data-testid="save-context-btn"
                        className="px-3 py-2 text-xs border border-white/20 text-white hover:bg-white/[0.04] disabled:opacity-40 inline-flex items-center gap-1.5 whitespace-nowrap">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                    </button>
                    <button onClick={lockSnapshot} disabled={busy}
                        data-testid="lock-snapshot-btn"
                        title="Snapshot the current working copy as a new immutable version"
                        className="px-3 py-2 text-xs bg-blue-500/20 border border-blue-500/40 text-blue-200 hover:bg-blue-500/30 disabled:opacity-40 inline-flex items-center gap-1.5 whitespace-nowrap">
                        <Lock className="w-3 h-3" /> Snapshot
                    </button>
                </div>
            </div>

            {/* Tab strip */}
            <div className="flex border-y border-border/40 py-2 gap-1 flex-wrap">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`context-tab-${t.id}`}
                        className={cls(
                            "px-2.5 py-1 text-xs font-mono uppercase inline-flex items-center gap-1.5",
                            tab === t.id ? "text-white" : "text-gray-500 hover:text-white"
                        )}>
                        <t.icon className="w-3 h-3" /> {t.label}
                    </button>
                ))}
                <span className="ml-auto text-[10px] font-mono text-gray-600 self-center">
                    rev {data.materials?.revision || 1} • {data.versions?.length || 0} snapshot{(data.versions?.length || 0) === 1 ? "" : "s"}
                </span>
            </div>

            {/* Body */}
            <div className="border border-border/40 bg-card p-5 space-y-4 min-h-[420px]" data-testid={`context-body-${tab}`}>
                {tab !== "documents" ? (
                    (FIELDS_BY_TAB[tab] || []).map(([key, label, type, opts]) => (
                        <Field key={key} label={label}>
                            <Editor type={type} value={draft[key] ?? ""} options={opts}
                                onChange={(v) => setDraft(prev => ({ ...prev, [key]: v }))}
                                testid={`ctx-${key}`} />
                        </Field>
                    ))
                ) : (
                    <DocumentsTab
                        docs={data.materials?.documents || []}
                        onUpload={upload}
                        onRemove={removeDoc}
                        uploading={uploading}
                        uploadMsg={uploadMsg}
                        fileRef={fileRef}
                    />
                )}
            </div>

            {/* Versions */}
            {data.versions?.length > 0 && (
                <div className="border border-border/40 bg-card">
                    <div className="p-3 border-b border-border/40 text-xs font-mono uppercase text-gray-500">Snapshots</div>
                    <div>
                        {data.versions.map(v => (
                            <div key={v.id} className="p-3 border-b border-border/40 flex items-center justify-between text-xs" data-testid={`version-${v.id}`}>
                                <span className="font-mono text-gray-400">{new Date(v.locked_at).toLocaleString()}</span>
                                <span className="font-mono text-gray-500">rev {v.revision_at_lock}</span>
                                <span className="font-mono text-gray-600">{v.locked_to_cycle_id ? `cycle ${v.locked_to_cycle_id.slice(0, 8)}` : "manual snapshot"}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- Helpers ----------
function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase text-gray-500">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}

function Editor({ type, value, options, onChange, testid }) {
    if (type === "textarea") {
        return <textarea value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}
            className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1.5 focus:outline-none focus:border-white/30 min-h-[80px]" />;
    }
    if (type === "number") {
        return <input type="number" value={value ?? ""} onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))} data-testid={testid}
            className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1.5 font-mono focus:outline-none focus:border-white/30" />;
    }
    if (type === "select" && options?.options) {
        return (
            <select value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}
                className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1.5 focus:outline-none focus:border-white/30">
                <option value="">—</option>
                {options.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        );
    }
    return <input type="text" value={value || ""} disabled={options?.readOnly} onChange={e => onChange(e.target.value)} data-testid={testid}
        className={cls("w-full bg-background border border-border/40 text-sm text-white px-2 py-1.5 focus:outline-none focus:border-white/30", options?.readOnly && "opacity-60")} />;
}

function DocumentsTab({ docs, onUpload, onRemove, uploading, uploadMsg, fileRef }) {
    const [tag, setTag] = useState("scenario_flavor");
    return (
        <div className="space-y-4">
            <div>
                <p className="text-xs text-gray-500 mb-2">Upload supporting materials: org chart, employee handbook excerpts, strategy decks, prior reports. Tagged docs feed the Assessment Generator.</p>
                <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                        <Field label="Relevance tag">
                            <select value={tag} onChange={e => setTag(e.target.value)} data-testid="doc-tag"
                                className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1.5 focus:outline-none focus:border-white/30">
                                <option value="always_include">Always include</option>
                                <option value="scenario_flavor">Scenario flavor</option>
                                <option value="reference_only">Reference only</option>
                            </select>
                        </Field>
                    </div>
                    <div className="col-span-8">
                        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" data-testid="doc-file-input"
                            onChange={e => onUpload(e.target.files?.[0], tag)} className="hidden" />
                        <button onClick={() => fileRef.current?.click()} disabled={uploading}
                            data-testid="doc-upload-btn"
                            className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-xs border border-white/20 text-white hover:bg-white/[0.04] disabled:opacity-40">
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
                            {uploading ? "Uploading…" : "Upload document (PDF, DOCX, TXT, MD — max 25 MB)"}
                        </button>
                    </div>
                </div>
                {uploadMsg && <p className="text-[11px] text-gray-400 mt-2 font-mono">{uploadMsg}</p>}
            </div>

            <div>
                <p className="text-xs font-mono uppercase text-gray-500 mb-2">Document library ({docs.length})</p>
                {docs.length === 0 ? (
                    <p className="text-xs text-gray-600">No documents uploaded yet.</p>
                ) : (
                    <div className="border border-border/40">
                        {docs.map(d => (
                            <div key={d.id} className="p-3 border-b border-border/40 flex items-center gap-3 text-xs" data-testid={`doc-row-${d.id}`}>
                                <Files className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-white truncate">{d.filename}</p>
                                    <p className="text-[10px] font-mono text-gray-600">
                                        {(d.size_bytes / 1024).toFixed(1)} KB • {d.relevance_tag}
                                    </p>
                                </div>
                                <button onClick={() => onRemove(d.id)} data-testid={`doc-remove-${d.id}`}
                                    className="text-gray-500 hover:text-red-300">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
